import React from 'react';

import { useGetCard, useGetFace } from '@vlabs/api/artemy/client';
import { InfoCircleIcon, StraightArrowRightIcon } from '@vlabs/icons';
import validate from '@vlabs/shared/validators';
import { Control } from '@vlabs/uikit';
import type { TOption } from '@vlabs/uikit/types';
import { routes } from '@vlapps/point3/src/app/routes';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { generatePath, Link, useParams } from 'react-router-dom';

import st from './LinkedToCardFaceFields.module.sass';

interface LinkedToCardFaceFieldsPoint3Props {
  listOptions: TOption[];
}

export function LinkedToCardFaceFieldsPoint3({
  listOptions,
}: LinkedToCardFaceFieldsPoint3Props): React.JSX.Element {
  const { t } = useTranslation();
  const { faceId } = useParams<{ faceId: string }>();

  const getFace = useGetFace(faceId);
  const cardId = getFace.data?.card_id;
  // @ts-expect-error enabled
  const getCard = useGetCard(cardId, { targets: ['user_data', 'lists'] }, {
    query: {
      enabled: !!cardId,
    },
  });
  const personCardData = getCard?.data;
  const personCardFio = (personCardData?.user_data as { fio?: string } | undefined)?.fio;
  const lists = personCardData?.lists ?? [];

  const {
    register,
    formState: { errors },
  } = useFormContext();

  return (
    <div className={st.Root}>
      <div className={st.CardInfo}>
        <div className={st.CardInfoContent}>
          <span className={st.MetaLabel}>
            {t('face-page:edit_face_info.card_label')}
          </span>

          {!!personCardFio && (
            <span className={st.CardTitle}>
              {personCardFio}
            </span>
          )}

          <span className={st.MetaLabel}>
            {cardId}
          </span>
        </div>

        {!!cardId && (
          <Control.IconButton
            as={Link}
            icon={<StraightArrowRightIcon />}
            kind="secondary"
            to={generatePath(routes.externalId.read, { externalId: cardId })}
          />
        )}
      </div>

      <Control.Textarea
        data-testid="faceCreateFormTextarea"
        id="user_data"
        label={t('face-page:edit_face_info.information_label')}
        {...register('user_data', {
          maxLength: validate.maxLength(128),
        })}
        errors={errors}
      />

      <div className={st.CardListsSection}>
        <div className={st.CardListsRow}>
          <div className={st.CardListsLabel}>
            {t('face-page:edit_face_info.lists_label')}
          </div>

          <div className={st.CardLists}>
            {lists.map((listId, index) => (
              <React.Fragment key={listId}>
                {index > 0 && <span className={st.CardListsSeparator} />}
                <Control.Link
                  kind="secondary"
                  to={generatePath(routes.lists.read, { listId })}
                >
                  {listOptions?.find(({ value }) => value === listId)?.label ?? listId}
                </Control.Link>
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className={st.CardListsHint}>
          <InfoCircleIcon className={st.CardListsHintIcon} />
          <span>{t('face-page:edit_face_info.lists_hint')}</span>
        </div>
      </div>
    </div>
  );
}
